import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Eye } from "lucide-react";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Candidate } from "@/types/candidate";
import { cn } from "@/lib/utils";

interface ResultsTableProps {
	results: Candidate[];
	isLoading: boolean;
	hasRun: boolean;
	selectedIds?: string[];
	onSelectionChange?: (ids: string[]) => void;
	notRunContent?: ReactNode;
	emptyContent?: ReactNode;
	/** Compact columns for People Search: Name, Function, Status + Open */
	dense?: boolean;
}

export function ResultsTable({
	results,
	isLoading,
	hasRun,
	selectedIds = [],
	onSelectionChange,
	notRunContent,
	emptyContent,
	dense = false,
}: ResultsTableProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const allSelected = results.length > 0 && selectedIds.length === results.length;
	const someSelected = selectedIds.length > 0 && selectedIds.length < results.length;

	const handleSelectAll = (checked: boolean) => {
		if (!onSelectionChange) return;
		if (checked)
			onSelectionChange(results.map(c => c.id));
		else
			onSelectionChange([]);
	};

	const handleSelectOne = (candidateId: string, checked: boolean) => {
		if (!onSelectionChange) return;
		if (checked)
			onSelectionChange([...selectedIds, candidateId]);
		else
			onSelectionChange(selectedIds.filter(id => id !== candidateId));
	};

	const openCandidate = (candidateId: string) => {
		navigate(`/candidates/${candidateId}`);
	};

	if (!hasRun)
		return (
			notRunContent ?? (
				<div className="text-center py-12 text-muted-foreground">
					{t("queries.results.runPrompt", "Run a query to see results")}
				</div>
			)
		);

	if (isLoading)
		return (
			<div className="space-y-2">
				{Array.from({ length: 5 }).map((_, i) => (
					<Skeleton key={i} className={cn("w-full", dense ? "h-9" : "h-12")} />
				))}
			</div>
		);

	if (results.length === 0)
		return (
			emptyContent ?? (
				<div className="text-center py-12 text-muted-foreground">
					{t("queries.results.empty", "No candidates match your query")}
				</div>
			)
		);

	const headClass = dense ? "h-9 px-3" : undefined;
	const cellClass = dense ? "py-1.5 px-3" : undefined;

	return (
		<div className="border rounded-sm">
			<Table>
				<TableHeader>
					<TableRow>
						{onSelectionChange && (
							<TableHead className={cn("w-12", headClass)}>
								<Checkbox
									checked={allSelected ? true : someSelected ? "indeterminate" : false}
									onCheckedChange={(checked) => handleSelectAll(checked === true)}
									aria-label={t("queries.results.selectAll", "Select all")}
								/>
							</TableHead>
						)}
						<TableHead className={headClass}>{t("queries.results.columns.name", "Name")}</TableHead>
						{!dense && (
							<TableHead>{t("queries.results.columns.email", "Email")}</TableHead>
						)}
						<TableHead className={headClass}>{t("queries.results.columns.function", "Function")}</TableHead>
						<TableHead className={headClass}>{t("queries.results.columns.status", "Status")}</TableHead>
						{!dense && (
							<>
								<TableHead>{t("queries.results.columns.skills", "Skills")}</TableHead>
								<TableHead>{t("queries.results.columns.location", "Location")}</TableHead>
							</>
						)}
						{dense && (
							<TableHead className={cn("w-[1%] text-right", headClass)}>
								<span className="sr-only">{t("queries.results.open", "Open")}</span>
							</TableHead>
						)}
					</TableRow>
				</TableHeader>
				<TableBody>
					{results.map((candidate) => (
						<TableRow
							key={candidate.id}
							className={cn(!dense && "cursor-pointer hover:bg-muted/50")}
							data-state={selectedIds.includes(candidate.id) ? "selected" : undefined}
						>
							{onSelectionChange && (
								<TableCell className={cellClass} onClick={(e) => e.stopPropagation()}>
									<Checkbox
										checked={selectedIds.includes(candidate.id)}
										onCheckedChange={(checked) => handleSelectOne(candidate.id, !!checked)}
										aria-label={t("queries.results.selectCandidate", "Select {{name}}", { name: candidate.name })}
									/>
								</TableCell>
							)}
							{dense ? (
								<>
									<TableCell className={cn(cellClass, "font-medium max-w-[200px] truncate")}>
										{candidate.name}
									</TableCell>
									<TableCell className={cn(cellClass, "max-w-[220px] truncate text-muted-foreground")}>
										{candidate.function || "-"}
									</TableCell>
									<TableCell className={cellClass}>
										{candidate.status && (
											<Badge variant="outline" className="font-normal">
												{t(`candidates.status.${candidate.status}`, candidate.status)}
											</Badge>
										)}
									</TableCell>
									<TableCell className={cn(cellClass, "text-right")}>
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											onClick={() => openCandidate(candidate.id)}
											aria-label={t("queries.results.open", "Open")}
										>
											<Eye className="h-4 w-4" />
										</Button>
									</TableCell>
								</>
							) : (
								<>
									<TableCell
										className="font-medium"
										onClick={() => openCandidate(candidate.id)}
									>
										{candidate.name}
									</TableCell>
									<TableCell onClick={() => openCandidate(candidate.id)}>
										{candidate.email || "-"}
									</TableCell>
									<TableCell onClick={() => openCandidate(candidate.id)}>
										{candidate.function || "-"}
									</TableCell>
									<TableCell onClick={() => openCandidate(candidate.id)}>
										{candidate.status && (
											<Badge variant="outline">
												{t(`candidates.status.${candidate.status}`, candidate.status)}
											</Badge>
										)}
									</TableCell>
									<TableCell onClick={() => openCandidate(candidate.id)}>
										<div className="flex flex-wrap gap-1 max-w-[200px]">
											{(candidate.skills || []).slice(0, 3).map((skill) => (
												<Badge key={skill} variant="secondary" className="text-xs">
													{skill}
												</Badge>
											))}
											{(candidate.skills || []).length > 3 && (
												<Badge variant="secondary" className="text-xs">
													+{(candidate.skills || []).length - 3}
												</Badge>
											)}
										</div>
									</TableCell>
									<TableCell onClick={() => openCandidate(candidate.id)}>
										{candidate.location || "-"}
									</TableCell>
								</>
							)}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}
